import { element, func, oneOfType, string } from 'prop-types';
import { cloneElement, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import zoneContext from '../contexts/zone.js';
import useAPI from '../hooks/useAPI.js';
import { debug } from 'lib/debug.js';
import { filter } from 'rxjs';

export {
  RemoteControl
};

const log = debug.extend('remote-control');

RemoteControl.propTypes = {
  id: string.isRequired,
  onOver: func,
  onLeave: func,
  children: oneOfType([element, string]),
};

RemoteControl.defaultProps = {
  onOver: Function.prototype,
  onLeave: Function.prototype,
};

function RemoteControl(props) {
  const {
    id,
    children,
    onOver,
    onLeave,
    onClick,
  } = props;
  const API = useAPI();
  const {
    hostApp: {
      createControl,
      removeControl,
    },
  } = API;
  /**
   *
   * @type {React.MutableRefObject<HTMLElement>}
   */
  const ref = useRef();
  const hasReflection = useRef(false);
  const {
    zone_id,
    registerElement,
  } = useContext(zoneContext);

  const description = useMemo(() => {
    return {
      element_id: id,
      element_type: 'button',
      widget_type: 'proxy_button',
      zone_id,
    };
  }, [id, zone_id]);
  const events = useMemo(() => {
    return {
      click: () => {
        if (onClick) {
          onClick();
        } else {
          ref.current.click();
        }
      },
      over: () => {
        onOver();
        ref.current.dataset.hover = 'hover';
      },
      leave: () => {
        onLeave();
        delete ref.current.dataset.hover;
      },
    };
  }, [onClick, onLeave, onOver]);
  const position = useRef(null);

  const removeRemoteControl = useCallback((permanent = false) => {
    if (hasReflection.current) {
      removeControl({
        element: description,
      }, permanent);
      hasReflection.current = false;
    }
  }, [description, removeControl]);

  const setPosition = useCallback((element_position) => {
    removeRemoteControl();

    createControl({
      element: {
        ...description,
        element_position,
      },
      events,
    });
    hasReflection.current = true;

  }, [createControl, description, events, removeRemoteControl]);

  const updatePosition = useCallback((newPosition) => {
    const hasDiff = [
      newPosition?.top !== position.current?.top,
      newPosition?.left !== position.current?.left,
      newPosition?.width !== position.current?.width,
      newPosition?.height !== position.current?.height,
    ];
    if (hasDiff.some(Boolean)) {
      log(`update position of ${id}`, newPosition);

      setPosition(newPosition);
      position.current = newPosition;
    } else {
      log('all the same');
    }
  }, [id, setPosition]);

  useEffect(() => {
    return registerElement(ref.current, updatePosition);
  }, [registerElement, updatePosition]);

  useEffect(() => {
    const subscription = API.hostApp.message$.pipe(
      filter(({ element_id }) => id === element_id)
    ).subscribe((event) => {
      events[event.event_type]?.();
    });
    return () => {
      subscription.unsubscribe();
      removeRemoteControl(true);
    };
  }, [API.hostApp.message$, events, id, removeRemoteControl]);

  return cloneElement(children, { ref });
}
